import React from 'react';
import { Link } from 'react-router-dom';
import HeroSection from '../components/ui/HeroSection';
import AppIcon from '../components/AppIcon';
import '../styles/glass-card.css';
import '../styles/HeroSection.css';

const HomePage = () => {
  const featuresSectionRef = React.useRef(null);

  const handleScroll = () => {
    featuresSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    {
      title: 'Routine Analyser',
      description: 'Not sure if your skincare routine is doing its job? We’ll break it down and tell you what actually works!',
      link: '/skincare-routine-input',
      cta: 'Analyze Now',
      cardImage: '/HP1.svg'
    },
    {
      title: 'For Beginners',
      description: 'New to skincare? We’ll help you build a simple, effective routine from the ground up.',
      link: '/skin-assessment-questionnaire',
      cta: 'Start Assessment',
      cardImage: '/HP2.svg'
    },
    {
      title: 'My Dashboard',
      description: 'Track your progress, view your routine, and see personalized insights.',
      link: '/progress-tracking-dashboard',
      cta: 'View Dashboard',
      cardImage: '/HP3.svg'
    },
    {
      title: 'Product Recommendations',
      description: 'Discover products tailored to your skin goals and routine analysis.',
      link: '/product-recommendations',
      cta: 'Browse Products',
      cardImage: '/HP4.svg'
    },
    {
        title: 'Ingredient Hub',
        description: 'Learn about skincare ingredients and check for compatibility.',
        link: '/ingredient-education-hub',
        cta: 'Explore Ingredients',
        cardImage: '/HP5.svg'
    },
    {
        title: 'Skincare 101',
        description: 'The ABCs of Skincare. What You Need to Know',
        link: '/skincare-101',
        cta: 'Learn the Basics',
        cardImage: '/HP6.svg'
    }
  ];

  const aboutFeatures = [
      {
        icon: 'ClipboardList',
        title: 'Get a data-driven score on your routine'
      },
      {
        icon: 'Scan',
        title: 'Live analysis of your skin health'
      },
      {
        icon: 'BookOpen',
        title: 'Check ingredient compatibility'
      },
      {
        icon: 'TrendingUp',
        title: 'Visually track your skin improvements'
      }
  ]

  return (
    <div>
      <HeroSection />
        <section className="py-20 relative overflow-hidden">
            {/* Background blur element */}
            <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60rem] h-[60rem] bg-slate-200/50 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    {/* Text Content */}
                    <div className="p-8 bg-white/60 backdrop-blur-sm border border-slate-300 rounded-2xl">
                        <h2 className="text-3xl sm:text-4xl font-bold font-heading tracking-tight text-slate-800">A smarter way to care for your skin.</h2>
                        <p className="mt-4 text-lg text-slate-600">Inara X GRWM is a personalized skincare platform that combines cutting-edge technology with expert-level knowledge to provide a truly holistic skincare solution.</p>
                        <ul className="mt-8 space-y-4">
                            {aboutFeatures.map(feature => (
                                <li key={feature.title} className="flex items-center space-x-3">
                                    <div className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary">
                                        <AppIcon name={feature.icon} className="h-5 w-5" />
                                    </div>
                                    <span className="text-slate-700 font-medium">{feature.title}</span>
                                </li>
                          ))}
                        </ul>
                        <div className="mt-10">
                            <Link to="/about" className="text-primary font-semibold hover:underline">
                                Learn more about us &rarr;
                           </Link>
                        </div>
                    </div>
                    
                    {/* Video Content */}
                    <div className="relative h-96 w-full rounded-2xl overflow-hidden shadow-xl">
                      <video
                        className="absolute inset-0 h-full w-full object-cover"
                        src="/about.mp4"
                        autoPlay
                        loop
                        muted
                        playsInline
                      >
                        Your browser does not support the video tag.
                      </video>
                      <div className="absolute inset-0 bg-slate-900/20"></div>
                    </div>
                </div>
            </div>
            
            {/* Scroll Arrow */}
            <div className="relative z-10 arrow-container" onClick={handleScroll} style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)' }}>
              <div className="arrow"></div>
            </div>
         </section>


      <div className="text-foreground flex flex-col" ref={featuresSectionRef}>
          <main className="flex-grow pt-10 pb-12 sm:pt-20 sm:pb-16 bg-white relative z-10">
              {/* Grid Header */}
              <div className="text-center max-w-4xl mx-auto px-4 sm:px-6">
                 <h2 className="text-3xl sm:text-5xl lg:text-5xl font-heading font-bold text-slate-800">Your Personal Skincare OS</h2>
                  <p className="mt-4 text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
                      Achieve your skin goals with Science-backed Routine Analysis, Personalized Recommendations, and Progress Tracking.
                  </p>
              </div>

             {/* Feature Cards Grid */}
             <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-12 sm:mt-16">
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                     {features.map((feature) => (
                       <Link to={feature.link} key={feature.title} className="glass-card-container no-underline flex">
                         <div className="glass-card relative flex flex-col w-full">
                           <img
                             src={feature.cardImage}
                             alt=""
                             className="absolute w-[200px] h-[200px] object-contain rotate-[-15deg] right-[-20px] bottom-[-30px] opacity-5 invert"
                           />
                             <div className="relative z-10 flex flex-col flex-grow justify-between">
                               <div>
                                 <h3 className="text-xl sm:text-2xl font-heading font-semibold text-white">{feature.title}</h3>
                                 <p className="mt-2 text-sm sm:text-base text-slate-300">{feature.description}</p>
                               </div>
                               <div className="mt-6">
                                   <span className="text-slate-50 font-semibold text-sm sm:text-base">
                                       {feature.cta} &rarr;
                                   </span>
                               </div>
                             </div>
                         </div>
                       </Link>
                     ))}
                 </div>
             </div>

           {/* --- INARA X GRWM PHILOSOPHY SECTION --- */}
           <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-32 mb-16">
                <div className="text-center mb-16">
                  <span className="text-xl font-heading font-bold tracking-wider text-slate-400 uppercase">The Philosophy</span>
                  <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl font-heading">
                    Inara <span className="text-slate-400 px-1">x</span> GRWM
                  </h2>
                  <div className="w-24 h-1 bg-slate-200 mx-auto my-6 rounded-full"></div>
                  <p className="text-lg leading-relaxed text-slate-600 max-w-3xl mx-auto">
                  Good skin isn’t created by products alone, it’s shaped by your daily habits.
We translate real science into clear, transparent guidance you can actually use.
No hype, no shortcuts just truth, backed by evidence.
So your routine becomes something that genuinely works, not just something that sounds good.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 border-t border-slate-100 pt-12">
    {/* Point 1: Data/Analysis */}
    <div className="flex flex-col items-center text-center"> {/* Added items-center and text-center */}
        <div className="relative w-20 h-20 rounded-2xl overflow-hidden shadow-md mb-6 border border-slate-100 group">
            <img
                src="/data1.webp"
                alt="Data Analysis"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
        </div>
        <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">Data Over Hype</h3>
        <p className="text-slate-600 leading-relaxed">
            We don't guess. We analyze. Your routine is built on ingredient compatibility and skin-barrier health, not just trending products.
        </p>
    </div>

    {/* Point 2: Rituals */}
    <div className="flex flex-col items-center text-center"> {/* Added items-center and text-center */}
        <div className="relative w-20 h-20 rounded-2xl overflow-hidden shadow-md mb-6 border border-slate-100 group">
            <img
                src="/ritual.webp"
                alt="Skincare Ritual"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
        </div>
        <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">Holistic Rituals</h3>
        <p className="text-slate-600 leading-relaxed">
            True glow comes from consistency. We help you build sustainable habits that fit your lifestyle, making skincare a moment of mindfulness.
        </p>
    </div>

    {/* Point 3: Transparency/Ingredients */}
    <div className="flex flex-col items-center text-center"> {/* Added items-center and text-center */}
        <div className="relative w-20 h-20 rounded-2xl overflow-hidden shadow-md mb-6 border border-slate-100 group">
            <img
                src="/ing1.webp"
                alt="Pure Ingredients"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
        </div>
        <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">Ingredient Integrity</h3>
        <p className="text-slate-600 leading-relaxed">
            No hidden nasties. We decode complex ingredient lists so you know exactly what you are feeding your skin and why it matters.
        </p>
    </div>
</div>
 </div>

             {/* --- NEW COMMUNITY SECTION (Monochrome) --- */}
             <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-24 mb-8">
                <div className="relative rounded-3xl overflow-hidden bg-neutral-950 px-6 py-12 shadow-2xl sm:px-12 lg:px-16 border border-neutral-800">
                  {/* Decorative gradients - Monochrome */}
                  <div className="absolute inset-0 bg-gradient-to-br from-neutral-800/50 via-neutral-950/80 to-black" />
                  
                  {/* Subtle top-right white glow (low opacity) */}
                  <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
                  
                  {/* Subtle bottom-left gray glow */}
                  <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-64 h-64 bg-neutral-800/20 rounded-full blur-3xl" />

                  <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
                    <div className="max-w-2xl">
                       <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-neutral-300 text-sm mb-4">
                          <span className="w-2 h-2 rounded-full bg-emerald-500/80 animate-pulse"></span>
                          <span>Community Active Now</span>
                       </div>
                       <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl font-heading">
                         Join the Inara Community
                       </h2>
                       <p className="mt-4 text-lg text-neutral-400">
                         Skincare is better together. Join our WhatsApp group to share your journey, get exclusive tips, ask questions, and connect with fellow glow-getters.
                       </p>
                    </div>

                    <div className="flex-shrink-0">
                       <a
                         href="https://chat.whatsapp.com/CUelXVTjlDT3yy6LvtwVeb"
                         target="_blank"
                         rel="noopener noreferrer"
                         className="group inline-flex items-center justify-center px-8 py-4 text-base font-bold text-black transition-all duration-200 bg-white rounded-full hover:bg-neutral-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white shadow-lg hover:shadow-white/10"
                       >
                         <AppIcon name="Users" className="w-5 h-5 mr-2 text-black/70 group-hover:scale-110 transition-transform" />
                         Join WhatsApp Group
                       </a>
                    </div>
                  </div>
                </div>
             </div>
             {/* --- END COMMUNITY SECTION --- */}

          </main>
      </div>
    </div>
  );
};

export default HomePage;